import { Fragment } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IA_SERVICES, DIETARY, CUISINES } from "@/data/content";
import ArchDivider from "@/components/site/ArchDivider";
import Seo from "@/components/site/Seo";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

export default function IndianAlchemy() {
  return (
    <div className="bg-[#FDFBF6]">
      <Seo
        title="Luxury Event Catering Services — Weddings, Cocktails, Destination Events | Indian Alchemy Gurugram"
        description="Indian Alchemy — premium wedding catering in Gurugram & Delhi NCR. Curated menus for weddings, cocktail evenings, high teas, destination weddings, and HNI events. FSSAI-certified caterer."
        path="/indian-alchemy"
        keywords="luxury event catering Gurugram, wedding catering Delhi NCR, destination wedding catering Delhi, cocktail catering Gurugram, high tea catering Delhi NCR, premium catering services Gurgaon, FSSAI certified caterer Gurgaon"
      />
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
            Indian Alchemy™ · Premium Event Catering
          </div>
          <h1 className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#3B1F1A] max-w-5xl">
            Curated. Rooted.<br />
            <em className="italic text-[#C9A961]">Elevated.</em>
          </h1>
          <p className="mt-8 text-[#3B1F1A]/75 leading-relaxed max-w-2xl">
            Luxury event catering in Gurugram &amp; Delhi NCR — nine ways we
            build a night. Every service is briefed against the room —
            venue, guest list, cuisine, service style. Nothing here is a
            package you pick from a menu.
          </p>
        </div>
      </section>

      {/* FULL-BLEED EDITORIAL SERVICE BANDS */}
      <section className="relative">
        {IA_SERVICES.map((s, i) => (
          <Fragment key={s.n}>
            <motion.article
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-120px" }}
              className="relative min-h-[75vh] md:min-h-[85vh] w-full overflow-hidden bg-[#1A100D]"
            >
              <img
                src={s.img}
                alt={`${s.title} — Indian Alchemy luxury event catering, Gurugram & Delhi NCR`}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-[#1A100D]/72" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A100D]/85 via-[#1A100D]/25 to-[#1A100D]/60 pointer-events-none" />
              <div
                aria-hidden
                className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")",
                }}
              />

              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 min-h-[75vh] md:min-h-[85vh] flex items-center py-20 md:py-32">
                <div
                  className={`max-w-2xl text-[#FDFBF6] ${
                    i % 2 === 0 ? "" : "md:ml-auto md:text-right"
                  }`}
                >
                  <div className="font-mono text-[11px] tracking-[0.32em] uppercase text-[#C9A961]">
                    Chapter {s.n}
                  </div>
                  <h2 className="mt-6 font-serif font-light text-5xl md:text-7xl leading-[1.02] text-[#FDFBF6]">
                    {s.title}
                  </h2>
                  <p className="mt-6 text-[#FDFBF6]/85 leading-relaxed max-w-xl md:text-lg">
                    {s.body}
                  </p>

                  {s.tiers && (
                    <div className={`mt-8 grid grid-cols-3 gap-3 max-w-lg ${i % 2 === 0 ? "" : "md:ml-auto"}`}>
                      {s.tiers.map((t) => (
                        <div
                          key={t.name}
                          className="p-4 bg-[#FDFBF6]/8 backdrop-blur-sm border border-[#C9A961]/40 text-left"
                        >
                          <div className="text-[10px] uppercase tracking-[0.24em] text-[#C9A961]">
                            {t.name}
                          </div>
                          <div className="mt-1 font-serif text-2xl text-[#FDFBF6]">
                            {t.price}
                          </div>
                          <div className="text-[11px] text-[#FDFBF6]/70">{t.desc}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <Link
                    to="/book"
                    className={`mt-10 inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-[#FDFBF6] border-b border-[#C9A961] pb-1 hover:text-[#C9A961] ${
                      i % 2 === 0 ? "" : "md:justify-self-end"
                    }`}
                  >
                    Design this for my event <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </motion.article>

            {/* Gold arch divider between service bands */}
            {i < IA_SERVICES.length - 1 && (
              <div className="bg-[#FDFBF6] py-10 md:py-14 flex justify-center text-[#C9A961]">
                <ArchDivider />
              </div>
            )}
          </Fragment>
        ))}
      </section>

      {/* CUISINE EXPERTISE — circular photos */}
      <section className="relative bg-[#F5C9B0]/25 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
              Cuisine Expertise
            </div>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl leading-[1.05] text-[#3B1F1A]">
              Six kitchens. <em className="italic text-[#C9A961]">One brigade.</em>
            </h2>
            <p className="mt-5 text-[#3B1F1A]/70 leading-relaxed">
              Our chefs move between idioms with respect, not remix. Every course has a lineage — and a reason to be on the plate.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-10">
            {CUISINES.map((c, i) => (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.06, duration: 0.7 }}
                className="flex flex-col items-center text-center group"
              >
                <div className="relative w-32 h-32 md:w-36 md:h-36 rounded-full overflow-hidden ring-1 ring-[#C9A961]/40 ring-offset-4 ring-offset-[#FDF5EE] transition-transform duration-500 group-hover:scale-105">
                  <img
                    src={c.img}
                    alt={`${c.label} cuisine — served by Indian Alchemy catering brigade`}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-[#3B1F1A]/0 group-hover:bg-[#3B1F1A]/25 transition-colors" />
                </div>
                <div className="mt-5 font-serif text-xl text-[#3B1F1A]">{c.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DIETARY NOTE */}
      <section className="bg-[#FDFBF6] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="flex justify-center mb-8 text-[#C9A961]">
            <ArchDivider />
          </div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
            Dietary accommodations
          </div>
          <h3 className="mt-6 font-serif font-light text-3xl md:text-4xl text-[#3B1F1A] leading-tight">
            We plate for every table.
          </h3>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {DIETARY.map((d) => (
              <span
                key={d}
                className="inline-flex items-center h-10 px-5 border border-[#3B1F1A]/20 rounded-full text-sm text-[#3B1F1A]"
              >
                {d}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-[#1A100D] text-[#FDFBF6] py-28 md:py-40 overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-15 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center relative">
          <div className="flex justify-center mb-10 text-[#C9A961]">
            <ArchDivider />
          </div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
            The last chapter is yours
          </div>
          <h2 className="mt-8 font-serif font-light text-5xl md:text-7xl leading-[1.02]">
            Let&rsquo;s create <em className="italic text-[#C9A961]">something extraordinary</em> together.
          </h2>
          <p className="mt-8 max-w-xl mx-auto text-[#FDFBF6]/75 leading-relaxed">
            Send us the room, the guest list, and one dish you want people to remember. We&rsquo;ll take it from there.
          </p>
          <Link
            to="/book"
            className="mt-12 inline-flex items-center gap-3 h-14 px-8 rounded-full bg-[#C9A961] text-[#3B1F1A] text-xs uppercase tracking-[0.32em] hover:bg-[#FDFBF6] transition-colors"
          >
            Book a Consultation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
